import React from 'react'
import cx from 'classnames'
import { useAppSelector } from 'reduxTyped'
import { l, palettes } from '@manychat/manyui'

import { MessageTemplateAttachment } from 'apps/whatsApp/core/constants'
import { isHeaderWithAttachment } from 'apps/whatsApp/MessageTemplates/helpers'
import { getTemplateByNameAndLanguageSelector } from 'apps/whatsApp/redux/messageTemplatesSlice'
import {
  AttachmentBlockAttachmentIconsMap,
  AttachmentBlockAttachmentType,
} from 'common/builder/blocks/blockConstants'
import { IBlock } from 'common/builder/blocks/blockInterfaces'
import FlowTextEditor from 'common/builder/components/misc/TextInput'
import BlockButtonList from 'common/builder/controllers/builder/BlockButtonList'
import {
  WaMessageTemplate,
  WaMessageTemplateHeaderType,
} from 'shared/api/requests/whatsApp/schemas'

import cm from './WhatsAppMessageTemplate.module.css'

const typesRelations: Partial<Record<MessageTemplateAttachment, AttachmentBlockAttachmentType>> = {
  [MessageTemplateAttachment.DOCUMENT]: AttachmentBlockAttachmentType.FILE,
  [MessageTemplateAttachment.VIDEO]: AttachmentBlockAttachmentType.VIDEO,
}

interface Props {
  builderId: string
  block: IBlock
}

export const getMessageText = ({ header, body, footer }: WaMessageTemplate): string => {
  const footerStr = footer ? `\n${footer}` : ''
  const headerStr =
    isHeaderWithAttachment(header) &&
    header?.type === WaMessageTemplateHeaderType.TEXT &&
    header.text
      ? `${header.text}\n`
      : ''

  return `${headerStr}${body}${footerStr}`
}

const WhatsAppMessageTemplate = ({ builderId, block }: Props) => {
  const template = useAppSelector((state) =>
    getTemplateByNameAndLanguageSelector(
      state,
      'name' in block && block.name ? block.name : '',
      'language' in block && block.language ? block.language : null,
    ),
  )

  if (!template) {
    return (
      <div
        data-test-id="template-not-found"
        className={cx(cm.notFoundWrapper, 'p-a b-a d-flex justify-center align-center')}
      >
        <span>{l.translate('Template not found')}</span>
      </div>
    )
  }

  const textWrapperClass = cx({
    [cm.withoutButtons]: template.buttons.length === 0,
    [cm.withHeader]: isHeaderWithAttachment(template.header),
  })
  const theme = textWrapperClass ? { wrapper: textWrapperClass } : undefined

  const iconType =
    isHeaderWithAttachment(template.header) &&
    template.header &&
    typesRelations[template.header.type]
  const Icon = iconType ? AttachmentBlockAttachmentIconsMap[iconType] : ''

  return (
    <div className="m-x">
      <label className="form-label m-b m-t-0">{template.name_human}</label>
      {isHeaderWithAttachment(template.header) && (
        <>
          {template.header?.type === WaMessageTemplateHeaderType.IMAGE && (
            <img src={template.header.url} className={cm.img} />
          )}

          {(template.header?.type === WaMessageTemplateHeaderType.VIDEO ||
            template.header?.type === WaMessageTemplateHeaderType.DOCUMENT) && (
            <div className={cx(cm.attachment, 'p-x b-a-2')}>
              {Icon && <Icon className="m-b-xxs" size={32} color={palettes.typography.secondary} />}
              <div className={'text-md text-center'}>{template.header.name}</div>
            </div>
          )}
        </>
      )}
      <FlowTextEditor
        builderId={builderId}
        value={getMessageText(template)}
        theme={theme}
        disabled
      />

      <BlockButtonList builderId={builderId} blockId={block.id} allowAdd={false} lastRound />
    </div>
  )
}

export default WhatsAppMessageTemplate
